import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Row,
  Col,
  Input,
  Select,
  Typography,
  DatePicker,
} from 'antd';
import * as XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import { TrainingsApi } from '../../Api/TrainingsApi';
import TmMasterTable from '../ReusableComponents/TmMasterTable';
import { TrainingsCalendarColumns } from '../../constant/tableHeader';
import { downloadTrainingsExcel } from '../ReusableComponents/downloadtraningsexcel';
import { toastError, toastSuccess, toastWarn } from '../Toast/ToastComponent';

const { Text } = Typography;

const FIELD_LABEL = {
  title: 'Title',
  subTitle: 'Sub Title',
  docNo: 'Doc No',
  docNo2: 'Doc No',
  revisionNo: 'Revision No',
  releaseDate: 'Release Date',
  companyName: 'Company Name',
  calendarYear: 'Annual Training Plan & Calendar Year',
  month: 'Month',
  docNoMonth: 'Doc No (Month)',
};

const TrainingsCalenderView = forwardRef((props, ref) => {
  const [headerInfo, setHeaderInfo] = useState({
    title: '',
    subTitle: '',
    docNo: '',
    docNo2: '',
    revisionNo: '',
    releaseDate: dayjs(),
    companyName: 'Openwave Computing Service Private Limited',
    calendarYear: `${dayjs().year()}-${dayjs().year() + 1}`,
    month: dayjs().format('MMMM'),
    docNoMonth: '',
  });

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);

  useImperativeHandle(ref, () => ({
    handleExportExcel,
    handleDownloadPDF,
  }));

  useEffect(() => {
    fetchCalendarData(headerInfo.calendarYear, headerInfo.month);
  }, []);

const handleHeaderChange = (field, value) => {
  setHeaderInfo((prev) => ({ ...prev, [field]: value }));
};
  const fetchCalendarData = async (year, month) => {
    setLoading(true);
    try {
      const res = await TrainingsApi.getcalendarList({ year, month });
      const raw = res?.data?.result || res?.data || [];
      const formatted = raw.map((item, index) => ({
        id: item.id || index,
        key: item.id || index,
        sNo: index + 1,
        category: item.category || '--',
        type: item.type || '--',
        trainingName: item.training_name || '--',
        trainers: item.trainers || '--',
        days: item.training_days?.days ? `${item.training_days.days} Days` : '--',
        participants: item.participants || '--',
        trainees: item.no_of_trainees || '0',
        date_of_training: item.date_of_training || '--',
        status: (item.training_status === 'COMPLETED' ? 'completed' : 
                 item.training_status === 'ONGOING' ? 'ongoing' : 
                 'ongoing'),
        feedback_date: item.feedback_date || 'Nil',
        evaluation_date: item.evaluation_date || 'Nil',
      }));
      setTableData(formatted);
    } catch (error) {
      console.error('API Error:', error);
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

const handleExportExcel = () => {
  const wb = XLSX.utils.book_new();

  // =========================
  // DATA BUILD
  // =========================
  const excelData = [
    ['TRAINING CALENDAR REPORT'],
    [],

    [
      'Title', headerInfo.title || '--',
      'Sub Title', headerInfo.subTitle || '--',
      'Doc No', headerInfo.docNo || '--',
    ],

    [
      'Doc No 2', headerInfo.docNo2 || '--',
      'Revision No', headerInfo.revisionNo || '--',
     'Release Date',
headerInfo.releaseDate
  ? dayjs(headerInfo.releaseDate).format('DD-MM-YYYY')
  : '--',
    ],

    [
      'Company Name', headerInfo.companyName || '--',
      'Calendar Year', headerInfo.calendarYear || '--',
      'Month', headerInfo.month || '--',
    ],

    [
      'Doc No (Month)', headerInfo.docNoMonth || '--',
    ],

    [],

    [
      'S.No',
      'Category',
      'Type',
      'Name Of Training',
      'Trainers',
      'Training Days',
      'Participants',
      'No of Trainees',
      'Date Of Training',
      'Status',
      'Feedback Date',
    ],

    ...tableData.map((item) => [
      String(item.sNo || ''),
      String(item.category || ''),
      String(item.type || ''),
      String(item.trainingName || ''),
      String(item.trainers || ''),
      String(item.days || ''),
      String(item.participants || ''),
      String(item.trainees || ''),
      String(item.date_of_training || ''),
      String(item.status || ''),
      String(item.feedback_date || ''),
    ]),
  ];

  const ws = XLSX.utils.aoa_to_sheet(excelData);

  // =========================
  // MERGE TITLE
  // =========================
  ws['!merges'] = [
    {
      s: { r: 0, c: 0 },
      e: { r: 0, c: 10 },
    },
  ];

  // =========================
  // RANGE
  // =========================
  const range = XLSX.utils.decode_range(ws['!ref']);

  // =========================
  // CELL STYLING
  // =========================
  for (let R = range.s.r; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = ws[cellAddress];

      if (!cell) continue;

      cell.t = 's';

      // TITLE
      if (R === 0) {
        cell.s = {
          font: { bold: true, sz: 16, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '1F4E78' } },
          alignment: { horizontal: 'center', vertical: 'center' },
        };
      }

      // HEADER INFO (2-column layout)
      else if (R >= 2 && R <= 5) {
        const isLabel = C % 2 === 0;

        cell.s = {
          font: { bold: isLabel, sz: 11 },
          fill: { fgColor: { rgb: isLabel ? 'D9EAF7' : 'FFFFFF' } },
          alignment: {
            horizontal: 'left',
            vertical: 'center',
            wrapText: true,
          },
          border: {
            top: { style: 'thin', color: { rgb: 'D9D9D9' } },
            bottom: { style: 'thin', color: { rgb: 'D9D9D9' } },
            left: { style: 'thin', color: { rgb: 'D9D9D9' } },
            right: { style: 'thin', color: { rgb: 'D9D9D9' } },
          },
        };
      }

      // TABLE HEADER
      else if (R === 7) {
        cell.s = {
          font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '1F4E78' } },
          alignment: {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true,
          },
          border: {
            top: { style: 'thin', color: { rgb: 'FFFFFF' } },
            bottom: { style: 'thin', color: { rgb: 'FFFFFF' } },
            left: { style: 'thin', color: { rgb: 'FFFFFF' } },
            right: { style: 'thin', color: { rgb: 'FFFFFF' } },
          },
        };
      }

      // TABLE BODY
      else {
        cell.s = {
          font: { sz: 11 },
          alignment: {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true,
          },
          fill: {
            fgColor: { rgb: R % 2 === 0 ? 'F8FAFC' : 'FFFFFF' },
          },
          border: {
            top: { style: 'thin', color: { rgb: 'D9D9D9' } },
            bottom: { style: 'thin', color: { rgb: 'D9D9D9' } },
            left: { style: 'thin', color: { rgb: 'D9D9D9' } },
            right: { style: 'thin', color: { rgb: 'D9D9D9' } },
          },
        };
      }
    }
  }

  // =========================
  // AUTO COLUMN WIDTH 🚀
  // =========================
  const colWidths = [];

  for (let C = range.s.c; C <= range.e.c; C++) {
    let maxLength = 10;

    for (let R = range.s.r; R <= range.e.r; R++) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];

      if (cell && cell.v) {
        maxLength = Math.max(maxLength, String(cell.v).length);
      }
    }

    colWidths.push({
      wch: Math.min(maxLength + 2, 50),
    });
  }

  ws['!cols'] = colWidths;

  // =========================
  // AUTO ROW HEIGHT + HEADER HEIGHT FIX 🚀
  // =========================
  const rowHeights = [];

  for (let R = range.s.r; R <= range.e.r; R++) {
    let maxLines = 1;

    for (let C = range.s.c; C <= range.e.c; C++) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];

      if (cell && cell.v) {
        const text = String(cell.v);
        const lines = Math.ceil(text.length / 30);
        maxLines = Math.max(maxLines, lines);
      }
    }

    let height;

    if (R === 0) {
      height = 40; // TITLE
    } else if (R >= 2 && R <= 5) {
      height = 38; // HEADER INFO (2-column layout)
    } else if (R === 7) {
      height = 45; // TABLE HEADER
    } else {
      height = maxLines * 15; // BODY
    }

    rowHeights.push({ hpt: height });
  }

  ws['!rows'] = rowHeights;

  // =========================
  // EXPORT
  // =========================
  XLSX.utils.book_append_sheet(wb, ws, 'Training Calendar');

  XLSX.writeFile(
    wb,
    `Training_Calendar_${headerInfo.month || 'Report'}.xlsx`
  );
};

  const getMonthDayjs = (monthName) => {
    if (!monthName) return null;
    return dayjs(monthName, 'MMMM');
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF('l', 'pt', 'a4');

    autoTable(doc, {
      startY: 30,
      theme: 'grid',
      styles: {
        fontSize: 9,
        cellPadding: 8,
        lineColor: [200, 200, 200],
        lineWidth: 0.5,
      },
      columnStyles: {
        0: { cellWidth: 120, fillColor: [245, 247, 250], fontStyle: 'bold' },
        1: { cellWidth: 150 },
        2: { cellWidth: 120, fillColor: [245, 247, 250], fontStyle: 'bold' },
        3: { cellWidth: 150 },
        4: { cellWidth: 120, fillColor: [245, 247, 250], fontStyle: 'bold' },
        5: { cellWidth: 150 },
      },
      body: [
        [
          FIELD_LABEL.title, headerInfo.title || '--',
          FIELD_LABEL.subTitle, headerInfo.subTitle || '--',
          FIELD_LABEL.docNo, headerInfo.docNo || '--',
        ],
        [
          FIELD_LABEL.docNo2, headerInfo.docNo2 || '--',
          FIELD_LABEL.revisionNo, headerInfo.revisionNo || '--',
       FIELD_LABEL.releaseDate,
headerInfo.releaseDate
  ? dayjs(headerInfo.releaseDate).format('DD-MM-YYYY')
  : '--',
        ],
        [
          FIELD_LABEL.companyName, headerInfo.companyName || '--',
          FIELD_LABEL.calendarYear, headerInfo.calendarYear || '--',
          FIELD_LABEL.month, headerInfo.month || '--',
        ],
        [
          FIELD_LABEL.docNoMonth, headerInfo.docNoMonth || '--',
          '', '', '', ''
        ],
      ],
    });

    const finalY = doc.lastAutoTable.finalY;

    autoTable(doc, {
      startY: finalY + 20,
      head: [
        [
          'S.No',
          'Category',
          'Type',
          'Name Of Training',
          'Trainers',
          'Training Days',
          'Name of participants',
          'No of Trainees',
          'Date of Training',
          'Status of Training',
          'Feedback Date',
          // 'Evaluation Date',
        ],
      ],
      body: tableData.map((r) => [
        r.sNo,
        r.category,
        r.type,
        r.trainingName,
        r.trainers,
        r.days,
        r.participants,
        r.trainees,
        r.date_of_training,
        r.status,
        r.feedback_date,
        // r.evaluation_date,
      ]),
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 6,
      },
      headStyles: {
        fillColor: [29, 53, 87],
        textColor: 255,
      },
    });

    doc.save(`Training_Calendar.pdf`);
  };

  const LabelText = ({ children }) => (
    <Text
      style={{
        display: 'block',
        marginBottom: 5,
        fontSize: '13px',
        color: '#595959',
        fontWeight: 500,
      }}
    >
      {children}
    </Text>
  );

  return (
    <div style={{ padding: '0', background: 'transparent' }}>
      <div style={{ background: '#fff', padding: '20px', borderRadius: '8px' }}>
        <Row gutter={[24, 16]}>
          <Col span={8}>
            <LabelText>{FIELD_LABEL.title}</LabelText>
            <Input value={headerInfo.title} placeholder="Enter Title" onChange={(e) => handleHeaderChange('title', e.target.value)} />
          </Col>
          <Col span={8}>
            <LabelText>{FIELD_LABEL.subTitle}</LabelText>
            <Input value={headerInfo.subTitle} placeholder="Enter Sub Title" onChange={(e) => handleHeaderChange('subTitle', e.target.value)} />
          </Col>
          <Col span={8}>
            <LabelText>{FIELD_LABEL.docNo}</LabelText>
            <Input value={headerInfo.docNo} placeholder="Enter Document Number" onChange={(e) => handleHeaderChange('docNo', e.target.value)} />
          </Col>
          {/* <Col span={8}>
            <LabelText>{FIELD_LABEL.docNo2}</LabelText>
            <Input value={headerInfo.docNo2} placeholder="Enter Document Number" onChange={(e) => handleHeaderChange('docNo2', e.target.value)} />
          </Col> */}
          <Col span={8}>
            <LabelText>{FIELD_LABEL.companyName}</LabelText>
            <Input value={headerInfo.companyName} placeholder="Enter Company Name" onChange={(e) => handleHeaderChange('companyName', e.target.value)} />
          </Col>
          <Col span={8}>
            <LabelText>{FIELD_LABEL.revisionNo}</LabelText>
            <Input value={headerInfo.revisionNo} placeholder="Enter Revision Number" onChange={(e) => handleHeaderChange('revisionNo', e.target.value)} />
          </Col>
         <Col span={8}>
  <LabelText>{FIELD_LABEL.releaseDate}</LabelText>

  <DatePicker
    style={{ width: '100%' }}
    format="DD-MM-YYYY"
    value={headerInfo.releaseDate}
    onChange={(date) => handleHeaderChange('releaseDate', date)}
  />
</Col>
          
          <Col span={8}>
            <LabelText>{FIELD_LABEL.calendarYear}</LabelText>
            <Select
              style={{ width: '100%' }}
              value={headerInfo.calendarYear}
              onChange={(val) => {
                handleHeaderChange('calendarYear', val);
                fetchCalendarData(val, headerInfo.month);
              }}
              options={[
                { label: '2020-2021', value: '2020-2021' },
                { label: '2021-2022', value: '2021-2022' },
                { label: '2022-2023', value: '2022-2023' },
                { label: '2023-2024', value: '2023-2024' },
                { label: '2024-2025', value: '2024-2025' },
                { label: '2025-2026', value: '2025-2026' },
                { label: '2026-2027', value: '2026-2027' },
              ]}
            />
          </Col>
          <Col span={4}>
            <LabelText>{FIELD_LABEL.month}</LabelText>
            <DatePicker
              picker="month"
              style={{ width: '100%' }}
              format="MMMM"
              value={getMonthDayjs(headerInfo.month)}
              onChange={(date) => {
                const month = date ? date.format('MMMM') : '';
                handleHeaderChange('month', month);
                fetchCalendarData(headerInfo.calendarYear, month);
              }}
            />
          </Col>
          <Col span={4}>
            <LabelText>{FIELD_LABEL.docNoMonth}</LabelText>
            <Input value={headerInfo.docNoMonth} onChange={(e) => handleHeaderChange('docNoMonth', e.target.value)} />
          </Col>
        </Row>
        <div style={{ marginTop: 30 }}>
          <TmMasterTable
            rows={tableData}
            columns={TrainingsCalendarColumns}
            custom={[]}
            hidePagination
            bordered
          />
        </div>
      </div>
    </div>
  );
});

export default TrainingsCalenderView;